import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import { WishlistProvider } from "./context/WishlistContext";
import { CartProvider } from "./context/CartContext";
import { NotificationsProvider } from "./context/NotificationsContext";
import { MessagesProvider } from "./context/MessagesContext";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import VisitorTracker from "./components/VisitorTracker";
import { UserType } from "./api/authService";

import ClientMainPage from "./Pages/Client/MainPage";
import ProductsList from "./Pages/Client/ProductsList";
import ProductDetails from "./Pages/Client/ProductDetails";
import StoresList from "./Pages/Client/StoresList";
import Wishlist from "./Pages/Client/Wishlist";
import Cart from "./Pages/Client/Cart";
import Checkout from "./Pages/Client/Checkout";
import ClientOrders from "./Pages/Client/Orders";
import ClientNotifications from "./Pages/Client/Notifications";
import ClientMessages from "./Pages/Client/Messages";
import Addresses from "./Pages/Client/Addresses";
import FollowedStores from "./Pages/Client/FollowedStores";
import MyCoupons from "./Pages/Client/MyCoupons";
import StoreNotifications from "./Pages/Client/StoreNotifications";

import AdminLogin from "./Pages/Admin/Login";
import AdminLayout from "./Pages/Admin/AdminLayout";
import Users from "./Pages/Admin/Users";
import AccountAccess from "./Pages/Admin/AccountAccess";
import ClientWarnings from "./Pages/Admin/ClientWarnings";
import WholesaleClients from "./Pages/Admin/WholesaleClients";
import ProductApprovals from "./Pages/Admin/ProductApprovals";
import StoreProducts from "./Pages/Admin/StoreProducts";
import PartnerRequests from "./Pages/Admin/PartnerRequests";
import Orders from "./Pages/Admin/Orders";
import DeleteOrders from "./Pages/Admin/DeleteOrders";
import Delivery from "./Pages/Admin/Delivery";
import DeliveryCompanies from "./Pages/Admin/DeliveryCompanies";
import Categories from "./Pages/Admin/Categories";
import ProductImports from "./Pages/Admin/ProductImports";
import ScrapedProducts from "./Pages/Admin/ScrapedProducts";
import Attributes from "./Pages/Admin/Attributes";
import AuthenticityOptions from "./Pages/Admin/AuthenticityOptions";
import DeliveryPricing from "./Pages/Admin/DeliveryPricing";
import Cities from "./Pages/Admin/Cities";
import Advertisements from "./Pages/Admin/Advertisements";
import BackgroundImages from "./Pages/Admin/BackgroundImages";
import AboutUsMedia from "./Pages/Admin/AboutUsMedia";
import Coupons from "./Pages/Admin/Coupons";
import GiftOffers from "./Pages/Admin/GiftOffers";
import FeesSubscriptions from "./Pages/Admin/FeesSubscriptions";
import AdminDeliveryCompanyStoreFees from "./Pages/Admin/DeliveryCompanyStoreFees";
import Settings from "./Pages/Admin/Settings";
import Chats from "./Pages/Admin/Chats";
import Notifications from "./Pages/Admin/Notifications";
import Reports from "./Pages/Admin/Reports";

import BusinessOwnerLogin from "./Pages/BusinessOwner/Login";
import BusinessOwnerLayout from "./Pages/BusinessOwner/BusinessOwnerLayout";
import BusinessOwnerStoreLayoutDesigner from "./Pages/BusinessOwner/StoreLayoutDesigner";
import BusinessOwnerStoreCarousel from "./Pages/BusinessOwner/StoreCarousel";
import BusinessOwnerProducts from "./Pages/BusinessOwner/Products";
import BusinessOwnerProductDiscounts from "./Pages/BusinessOwner/ProductDiscounts";
import BusinessOwnerOrders from "./Pages/BusinessOwner/Orders";
import BusinessOwnerMessages from "./Pages/BusinessOwner/Messages";
import BusinessOwnerSettings from "./Pages/BusinessOwner/Settings";

import DeliveryLogin from "./Pages/Delivery/Login";
import DeliveryMainPage from "./Pages/Delivery/MainPage";

import DeliveryCompanyLogin from "./Pages/DelivryCompany/Login";
import DeliveryCompanyLayout from "./Pages/DelivryCompany/DeliveryCompanyLayout";
import DeliveryCompanyDashboard from "./Pages/DelivryCompany/Dashboard";
import DeliveryCompanyDrivers from "./Pages/DelivryCompany/Drivers";
import DeliveryCompanyOrders from "./Pages/DelivryCompany/Orders";
import DeliveryCompanyDriverAccess from "./Pages/DelivryCompany/DriverAccess";
import DeliveryCompanyFees from "./Pages/DelivryCompany/Fees";
import DeliveryCompanyStoreFees from "./Pages/DelivryCompany/StoreFees";
import DeliveryCompanySettings from "./Pages/DelivryCompany/Settings";

// Marketing pages are lazy so three.js / GSAP only load when they're visited.
const AboutUs = lazy(() => import("./Pages/AboutUs/AboutUs"));
// The admin dashboard pulls in three.js / GSAP for its 3D hero, so it gets its own chunk too.
const Dashboard = lazy(() => import("./Pages/Admin/Dashboard"));
const Analytics = lazy(() => import("./Pages/Admin/Analytics"));
const SocialMedia = lazy(() => import("./Pages/Admin/SocialMedia"));
const StoreAnalyticsDetail = lazy(() => import("./Pages/Admin/StoreAnalyticsDetail"));
const CityAnalyticsDetail = lazy(() => import("./Pages/Admin/CityAnalyticsDetail"));
const DistrictAnalyticsDetail = lazy(() => import("./Pages/Admin/DistrictAnalyticsDetail"));
const OrderDetail = lazy(() => import("./Pages/Admin/OrderDetail"));
const Stores = lazy(() => import("./Pages/Admin/Stores"));
const BusinessOwnerDashboard = lazy(() => import("./Pages/BusinessOwner/Dashboard"));
const BusinessOwnerStores = lazy(() => import("./Pages/BusinessOwner/Stores"));
const BusinessOwnerStoreProfile = lazy(() => import("./Pages/BusinessOwner/StoreProfile"));
const Join = lazy(() => import("./Pages/Join/Join"));
const marketingFallback = <div style={{ minHeight: "100vh", background: "#040d0a" }} />;

function App() {
  return (
    <LanguageProvider>
    <BrowserRouter>
    <AuthProvider>
      <VisitorTracker />
      <WishlistProvider>
        <CartProvider>
          <NotificationsProvider>
            <MessagesProvider>
            <Routes>
              <Route path="/" element={<ClientMainPage />} />
              <Route path="/products" element={<ProductsList />} />
              <Route path="/product/:id" element={<ProductDetails />} />
              <Route path="/stores" element={<StoresList />} />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/orders" element={<ClientOrders />} />
              <Route path="/notifications" element={<ClientNotifications />} />
              <Route path="/messages" element={<ClientMessages />} />
              <Route path="/addresses" element={<Addresses />} />
              <Route path="/followed-stores" element={<FollowedStores />} />
              <Route path="/my-coupons" element={<MyCoupons />} />
              <Route path="/store-notifications" element={<StoreNotifications />} />
              <Route
                path="/join"
                element={
                  <Suspense fallback={marketingFallback}>
                    <Join />
                  </Suspense>
                }
              />
              <Route
                path="/about-us-business"
                element={
                  <Suspense fallback={marketingFallback}>
                    <AboutUs />
                  </Suspense>
                }
              />
              {/* old address of the page above - keep links that were already shared working */}
              <Route path="/about-us" element={<Navigate to="/about-us-business" replace />} />

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredUserType={UserType.Admin} loginPath="/admin/login">
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route
                  path="dashboard"
                  element={
                    <Suspense fallback={null}>
                      <Dashboard />
                    </Suspense>
                  }
                />
                <Route
                  path="analytics"
                  element={
                    <Suspense fallback={null}>
                      <Analytics />
                    </Suspense>
                  }
                />
                <Route
                  path="analytics/stores/:id"
                  element={
                    <Suspense fallback={null}>
                      <StoreAnalyticsDetail />
                    </Suspense>
                  }
                />
                <Route
                  path="analytics/cities/:cityId"
                  element={
                    <Suspense fallback={null}>
                      <CityAnalyticsDetail />
                    </Suspense>
                  }
                />
                <Route
                  path="analytics/districts/:districtName"
                  element={
                    <Suspense fallback={null}>
                      <DistrictAnalyticsDetail />
                    </Suspense>
                  }
                />
                <Route
                  path="social-media"
                  element={
                    <Suspense fallback={null}>
                      <SocialMedia />
                    </Suspense>
                  }
                />
                <Route path="users" element={<Users />} />
                <Route path="account-access" element={<AccountAccess />} />
                <Route path="client-warnings" element={<ClientWarnings />} />
                <Route path="wholesale-clients" element={<WholesaleClients />} />
                <Route
                  path="stores"
                  element={
                    <Suspense fallback={null}>
                      <Stores />
                    </Suspense>
                  }
                />
                <Route path="product-approvals" element={<ProductApprovals />} />
                <Route path="store-products" element={<StoreProducts />} />
                <Route path="partner-requests" element={<PartnerRequests />} />
                <Route path="reports" element={<Reports />} />
                <Route path="orders" element={<Orders />} />
                <Route
                  path="orders/:id"
                  element={
                    <Suspense fallback={null}>
                      <OrderDetail />
                    </Suspense>
                  }
                />
                <Route path="delete-orders" element={<DeleteOrders />} />
                <Route path="delivery" element={<Delivery />} />
                <Route path="delivery-companies" element={<DeliveryCompanies />} />
                <Route path="delivery-companies/store-fees" element={<AdminDeliveryCompanyStoreFees />} />
                <Route path="categories" element={<Categories />} />
                <Route path="product-imports" element={<ProductImports />} />
                <Route path="scraped-products" element={<ScrapedProducts />} />
                <Route path="attributes" element={<Attributes />} />
                <Route path="authenticity-options" element={<AuthenticityOptions />} />
                <Route path="delivery-pricing" element={<DeliveryPricing />} />
                <Route path="cities" element={<Cities />} />
                <Route path="advertisements" element={<Advertisements />} />
                <Route path="background-images" element={<BackgroundImages />} />
                <Route path="about-us-media" element={<AboutUsMedia />} />
                <Route path="coupons" element={<Coupons />} />
                <Route path="gift-offers" element={<GiftOffers />} />
                <Route path="fees-subscriptions" element={<FeesSubscriptions />} />
                <Route path="chats" element={<Chats />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="settings" element={<Settings />} />
              </Route>

              <Route path="/business-owner/login" element={<BusinessOwnerLogin />} />
              <Route
                path="/business-owner"
                element={
                  <ProtectedRoute requiredUserType={UserType.BusinessOwner} loginPath="/business-owner/login">
                    <BusinessOwnerLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route
                  path="dashboard"
                  element={
                    <Suspense fallback={null}>
                      <BusinessOwnerDashboard />
                    </Suspense>
                  }
                />
                <Route
                  path="stores"
                  element={
                    <Suspense fallback={null}>
                      <BusinessOwnerStores />
                    </Suspense>
                  }
                />
                <Route
                  path="stores/:storeId"
                  element={
                    <Suspense fallback={null}>
                      <BusinessOwnerStoreProfile />
                    </Suspense>
                  }
                />
                <Route path="stores/:storeId/layout" element={<BusinessOwnerStoreLayoutDesigner />} />
                <Route path="stores/:storeId/carousel" element={<BusinessOwnerStoreCarousel />} />
                <Route path="products" element={<BusinessOwnerProducts />} />
                <Route path="products/discounts" element={<BusinessOwnerProductDiscounts />} />
                <Route path="orders" element={<BusinessOwnerOrders />} />
                <Route path="messages" element={<BusinessOwnerMessages />} />
                <Route path="settings" element={<BusinessOwnerSettings />} />
              </Route>

              <Route path="/delivery/login" element={<DeliveryLogin />} />
              <Route
                path="/delivery"
                element={
                  <ProtectedRoute requiredUserType={UserType.Delivery} loginPath="/delivery/login">
                    <DeliveryMainPage />
                  </ProtectedRoute>
                }
              />

              <Route path="/delivry-company/login" element={<DeliveryCompanyLogin />} />
              <Route
                path="/delivry-company"
                element={
                  <ProtectedRoute requiredUserType={UserType.DeliveryCompany} loginPath="/delivry-company/login">
                    <DeliveryCompanyLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<DeliveryCompanyDashboard />} />
                <Route path="drivers" element={<DeliveryCompanyDrivers />} />
                <Route path="orders" element={<DeliveryCompanyOrders />} />
                <Route path="driver-access" element={<DeliveryCompanyDriverAccess />} />
                <Route path="fees" element={<DeliveryCompanyFees />} />
                <Route path="store-fees" element={<DeliveryCompanyStoreFees />} />
                <Route path="settings" element={<DeliveryCompanySettings />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </MessagesProvider>
          </NotificationsProvider>
        </CartProvider>
      </WishlistProvider>
    </AuthProvider>
    </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
